import { useState, useEffect, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import {
  Users,
  Search,
  MoreHorizontal,
  CheckCircle,
  XCircle,
  Trash2,
  Shield,
  Clock,
  Ban,
  Loader2,
  RefreshCw,
  ArrowLeft,
} from "lucide-react"
import { Button } from "@/shared/components/ui/button"
import { Card, CardContent, CardHeader } from "@/shared/components/ui/card"
import { Input } from "@/shared/components/ui/input"
import { Badge } from "@/shared/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select"
import { Tabs, TabsList, TabsTrigger } from "@/shared/components/ui/tabs"
import { authService } from "@/shared/services/auth"
import { useAuthStore } from "@/shared/store/authStore"
import { useToast } from "@/shared/hooks/useToast"
import type { User } from "@/shared/types/user"

// 状态配置
const STATUS_CONFIG: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  pending: {
    label: "待审核",
    color: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
    icon: <Clock className="h-3 w-3" />,
  },
  active: {
    label: "已激活",
    color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
    icon: <CheckCircle className="h-3 w-3" />,
  },
  rejected: {
    label: "已拒绝",
    color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
    icon: <XCircle className="h-3 w-3" />,
  },
  disabled: {
    label: "已禁用",
    color: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400",
    icon: <Ban className="h-3 w-3" />,
  },
}

// 角色配置
const ROLE_CONFIG: Record<string, { label: string; color: string }> = {
  user: { label: "普通用户", color: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400" },
  developer: { label: "开发者", color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" },
  admin: { label: "管理员", color: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400" },
  super_admin: { label: "超级管理员", color: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400" },
}

export default function UserManagement() {
  const navigate = useNavigate()
  const { user: currentUser } = useAuthStore()
  const { showError, showSuccess } = useToast()

  // 状态
  const [users, setUsers] = useState<User[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState("")
  const [statusFilter, setStatusFilter] = useState<string>("all")
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)

  // 对话框状态
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [roleDialogOpen, setRoleDialogOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [selectedRole, setSelectedRole] = useState<string>("")
  const [isProcessing, setIsProcessing] = useState(false)

  // 加载用户列表
  const loadUsers = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await authService.getUsers({
        page,
        page_size: 10,
        status: statusFilter === "all" ? undefined : statusFilter,
        search: searchQuery || undefined,
      })

      if (response.success && response.data) {
        setUsers(response.data)
        setTotalPages(response.pagination?.total_pages || 1)
        setTotalCount(response.pagination?.total || 0)
      }
    } catch (error) {
      console.error("Failed to load users:", error)
      showError("加载用户列表失败")
    } finally {
      setIsLoading(false)
    }
  }, [page, statusFilter, searchQuery, showError])

  useEffect(() => {
    loadUsers()
  }, [loadUsers])

  // 审核用户
  const handleApprove = async (user: User) => {
    setIsProcessing(true)
    try {
      const result = await authService.updateUser(user.id, { status: "active" })
      if (result.success) {
        showSuccess(`已通过 ${user.name || user.email} 的审核`)
        loadUsers()
      } else {
        showError(result.error || "审核失败")
      }
    } catch (error) {
      showError("审核失败")
    } finally {
      setIsProcessing(false)
    }
  }

  const handleReject = async (user: User) => {
    setIsProcessing(true)
    try {
      const result = await authService.updateUser(user.id, { status: "rejected" })
      if (result.success) {
        showSuccess(`已拒绝 ${user.name || user.email} 的注册申请`)
        loadUsers()
      } else {
        showError(result.error || "操作失败")
      }
    } catch (error) {
      showError("操作失败")
    } finally {
      setIsProcessing(false)
    }
  }

  // 修改角色
  const handleOpenRoleDialog = (user: User) => {
    setSelectedUser(user)
    setSelectedRole(user.role as string)
    setRoleDialogOpen(true)
  }

  const handleChangeRole = async () => {
    if (!selectedUser || !selectedRole) return

    setIsProcessing(true)
    try {
      const result = await authService.updateUser(selectedUser.id, { role: selectedRole })
      if (result.success) {
        showSuccess(`已将 ${selectedUser.name || selectedUser.email} 的角色修改为 ${ROLE_CONFIG[selectedRole]?.label || selectedRole}`)
        setRoleDialogOpen(false)
        loadUsers()
      } else {
        showError(result.error || "修改角色失败")
      }
    } catch (error) {
      showError("修改角色失败")
    } finally {
      setIsProcessing(false)
    }
  }

  // 删除用户
  const handleOpenDeleteDialog = (user: User) => {
    setSelectedUser(user)
    setDeleteDialogOpen(true)
  }

  const handleDeleteUser = async () => {
    if (!selectedUser) return

    setIsProcessing(true)
    try {
      const result = await authService.deleteUser(selectedUser.id)
      if (result.success) {
        showSuccess(`已删除用户 ${selectedUser.name || selectedUser.email}`)
        setDeleteDialogOpen(false)
        loadUsers()
      } else {
        showError(result.error || "删除用户失败")
      }
    } catch (error) {
      showError("删除用户失败")
    } finally {
      setIsProcessing(false)
    }
  }

  // 禁用/启用用户
  const handleToggleStatus = async (user: User) => {
    const newStatus = user.status === "active" ? "disabled" : "active"
    setIsProcessing(true)
    try {
      const result = await authService.updateUser(user.id, { status: newStatus })
      if (result.success) {
        showSuccess(`已${newStatus === "active" ? "启用" : "禁用"}用户 ${user.name || user.email}`)
        loadUsers()
      } else {
        showError(result.error || "操作失败")
      }
    } catch (error) {
      showError("操作失败")
    } finally {
      setIsProcessing(false)
    }
  }

  // 格式化时间
  const formatDate = (dateString?: string) => {
    if (!dateString) return "-"
    return new Date(dateString).toLocaleDateString("zh-CN", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  // 获取状态 Badge
  const getStatusBadge = (status: string) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending
    return (
      <Badge variant="secondary" className={`${config.color} gap-1`}>
        {config.icon}
        {config.label}
      </Badge>
    )
  }

  // 获取角色 Badge
  const getRoleBadge = (role: string) => {
    const config = ROLE_CONFIG[role] || ROLE_CONFIG.user
    return (
      <Badge variant="secondary" className={config.color}>
        {config.label}
      </Badge>
    )
  }

  // 检查当前用户是否是管理员
  const isCurrentUserAdmin = currentUser?.role === "admin" || currentUser?.role === "super_admin"

  // 检查是否可以操作该用户
  const canOperateUser = (user: User) => {
    // 当前用户必须是管理员才能操作
    if (!isCurrentUserAdmin) return false
    // 不能操作自己
    if (user.id === currentUser?.id) return false
    // 不能操作超级管理员（除非自己也是超级管理员）
    if (user.role === "super_admin" && currentUser?.role !== "super_admin") return false
    return true
  }

  return (
    <div className="min-h-screen bg-background">
      {/* 顶部导航 */}
      <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-16 items-center gap-4 px-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/")}
            className="shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            <span className="text-lg font-semibold">用户管理</span>
          </div>
          <div className="flex-1" />
          <Button
            variant="outline"
            size="sm"
            onClick={loadUsers}
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? "animate-spin" : ""}`} />
            刷新
          </Button>
        </div>
      </header>

      {/* 主内容 */}
      <main className="container mx-auto px-4 py-8">
        <div className="space-y-6">
          {/* 标题和统计 */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">用户管理</h1>
              <p className="text-muted-foreground mt-1">
                共 {totalCount} 个用户
              </p>
            </div>
          </div>

          {/* 筛选和搜索 */}
          <Card>
            <CardHeader className="pb-4">
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                {/* 状态筛选 */}
                <Tabs value={statusFilter} onValueChange={setStatusFilter}>
                  <TabsList>
                    <TabsTrigger value="all">全部</TabsTrigger>
                    <TabsTrigger value="pending">
                      待审核
                    </TabsTrigger>
                    <TabsTrigger value="active">已激活</TabsTrigger>
                    <TabsTrigger value="rejected">已拒绝</TabsTrigger>
                    <TabsTrigger value="disabled">已禁用</TabsTrigger>
                  </TabsList>
                </Tabs>

                {/* 搜索框 */}
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="搜索邮箱或姓名..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* 用户表格 */}
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : users.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Users className="h-12 w-12 text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">暂无用户数据</p>
                </div>
              ) : (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>用户</TableHead>
                        <TableHead>角色</TableHead>
                        <TableHead>状态</TableHead>
                        <TableHead>注册时间</TableHead>
                        <TableHead className="text-right">操作</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell>
                            <div className="flex flex-col">
                              <span className="font-medium">
                                {user.name || user.nickname || "-"}
                              </span>
                              <span className="text-sm text-muted-foreground">
                                {user.email}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>{getRoleBadge(user.role as string)}</TableCell>
                          <TableCell>{getStatusBadge(user.status as string)}</TableCell>
                          <TableCell className="text-muted-foreground">
                            {formatDate(user.created_at)}
                          </TableCell>
                          <TableCell className="text-right">
                            {canOperateUser(user) ? (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon">
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  {/* 待审核用户的审核操作 */}
                                  {user.status === "pending" && (
                                    <>
                                      <DropdownMenuItem
                                        onClick={() => handleApprove(user)}
                                        disabled={isProcessing}
                                      >
                                        <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                                        通过审核
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        onClick={() => handleReject(user)}
                                        disabled={isProcessing}
                                      >
                                        <XCircle className="h-4 w-4 mr-2 text-red-600" />
                                        拒绝申请
                                      </DropdownMenuItem>
                                      <DropdownMenuSeparator />
                                    </>
                                  )}

                                  {/* 已激活用户的禁用操作 */}
                                  {user.status === "active" && (
                                    <DropdownMenuItem
                                      onClick={() => handleToggleStatus(user)}
                                      disabled={isProcessing}
                                    >
                                      <Ban className="h-4 w-4 mr-2" />
                                      禁用用户
                                    </DropdownMenuItem>
                                  )}

                                  {/* 已禁用用户的启用操作 */}
                                  {user.status === "disabled" && (
                                    <DropdownMenuItem
                                      onClick={() => handleToggleStatus(user)}
                                      disabled={isProcessing}
                                    >
                                      <CheckCircle className="h-4 w-4 mr-2" />
                                      启用用户
                                    </DropdownMenuItem>
                                  )}

                                  {/* 修改角色 */}
                                  <DropdownMenuItem
                                    onClick={() => handleOpenRoleDialog(user)}
                                    disabled={isProcessing}
                                  >
                                    <Shield className="h-4 w-4 mr-2" />
                                    修改角色
                                  </DropdownMenuItem>

                                  <DropdownMenuSeparator />

                                  {/* 删除用户 */}
                                  <DropdownMenuItem
                                    onClick={() => handleOpenDeleteDialog(user)}
                                    disabled={isProcessing}
                                    className="text-destructive focus:text-destructive"
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    删除用户
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            ) : (
                              <span className="text-xs text-muted-foreground">
                                {!isCurrentUserAdmin 
                                  ? "-" 
                                  : user.id === currentUser?.id 
                                    ? "当前用户" 
                                    : "超级管理员"}
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* 分页 */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1 || isLoading}
                  >
                    上一页
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    第 {page} / {totalPages} 页
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages || isLoading}
                  >
                    下一页
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* 删除确认对话框 */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>确认删除用户</DialogTitle>
            <DialogDescription>
              确定要删除用户 <strong>{selectedUser?.name || selectedUser?.email}</strong> 吗？
              此操作无法撤销。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={isProcessing}
            >
              取消
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteUser}
              disabled={isProcessing}
            >
              {isProcessing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  删除中...
                </>
              ) : (
                "确认删除"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 修改角色对话框 */}
      <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>修改用户角色</DialogTitle>
            <DialogDescription>
              修改 <strong>{selectedUser?.name || selectedUser?.email}</strong> 的角色
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={selectedRole} onValueChange={setSelectedRole}>
              <SelectTrigger>
                <SelectValue placeholder="选择角色" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="user">普通用户</SelectItem>
                <SelectItem value="developer">开发者</SelectItem>
                <SelectItem value="admin">管理员</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRoleDialogOpen(false)}
              disabled={isProcessing}
            >
              取消
            </Button>
            <Button onClick={handleChangeRole} disabled={isProcessing}>
              {isProcessing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  保存中...
                </>
              ) : (
                "保存"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

